import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Calendar, MapPin, Clock, User, Eye, Trash2, Mail, X } from 'lucide-react'
import { useSupabaseTestspielStore } from '../stores/supabaseTestspielStore'
import { useAuthStore } from '../stores/authStore'
import TestspielSlotsManager from '../components/TestspielSlotsManager'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

export default function TestspielePage() {
  const {
    testspiele, 
    loading, 
    error, 
    fetchTestspiele, 
    deleteTestspiel 
  } = useSupabaseTestspielStore()
  const { isAuthenticated, user, profile } = useAuthStore()
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming')
  const [selectedTestspiel, setSelectedTestspiel] = useState<any>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (isAuthenticated()) {
      console.log('🔄 Fetching testspiele...')
      fetchTestspiele()
    }
  }, [isAuthenticated])

  useEffect(() => {
    console.log('📊 Testspiele loaded:', testspiele.length, testspiele)
    console.log('🔍 Selected testspiel:', selectedTestspiel)
    console.log('👁️ Show modal state:', showModal)
    if (showModal && selectedTestspiel) {
      console.log('✅ MODAL SHOULD BE VISIBLE NOW!')
    }
  }, [testspiele, selectedTestspiel, showModal])

  const now = new Date()
  
  const filteredTestspiele = testspiele.filter(testspiel => {
    const testspielDate = new Date(testspiel.date)
    
    switch (filter) {
      case 'upcoming':
        return testspielDate >= now
      case 'past':
        return testspielDate < now
      default:
        return true
    }
  }).sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return filter === 'past' ? dateB - dateA : dateA - dateB
  })

  const handleDelete = async (id: string) => {
    console.log('🗑️ Delete button clicked for testspiel:', id)
    if (window.confirm('Soll dieses Testspiel wirklich gelöscht werden?')) {
      console.log('✅ User confirmed deletion')
      try {
        const result = await deleteTestspiel(id)
        if (result.error) {
          console.error('❌ Delete failed:', result.error)
          alert(`Fehler beim Löschen: ${result.error}`)
        } else {
          console.log('✅ Delete successful')
          alert('Testspiel wurde erfolgreich gelöscht!')
        }
      } catch (error: any) {
        console.error('❌ Delete error:', error)
        alert(`Fehler: ${error.message}`)
      }
    } else {
      console.log('❌ User cancelled deletion')
    }
  }

  const handleView = (testspiel: any) => {
    console.log('👁️ VIEW CLICKED - Testspiel ID:', testspiel.id)
    console.log('👁️ VIEW CLICKED - Setting modal state...')
    setSelectedTestspiel(testspiel)
    setShowModal(true)
    console.log('👁️ VIEW CLICKED - Modal should now be visible')
  }

  // const handleEdit = (testspielId: string) => {
  //   console.log('✏️ EDIT CLICKED - Testspiel ID:', testspielId)
  //   console.log('✏️ EDIT CLICKED - Navigating to edit page...')
  //   // Navigate to add page with edit mode
  //   navigate(`/testspiel/add?edit=${testspielId}`)
  // }

  const handleSendEmail = async (testspiel: any) => {
    console.log('📧 Email button clicked for testspiel:', testspiel.id)
    
    // Comment out old email functionality (Outlook opening)
    /*
    const subject = `Testspiel: ${testspiel.team} vs ${testspiel.opponent}`
    const body = `
Testspiel Details:

Team: ${testspiel.team}
Gegner: ${testspiel.opponent}
Datum: ${format(new Date(testspiel.date), 'PPP', { locale: de })}
Uhrzeit: ${testspiel.time}
Ort: ${testspiel.location || 'Vereinsplatz FC Frick'}
Trainer: ${testspiel.trainerName}

${testspiel.notes ? `Notizen: ${testspiel.notes}` : ''}

Kontakt:
${testspiel.contactPerson ? `Person: ${testspiel.contactPerson}` : ''}
${testspiel.contactEmail ? `E-Mail: ${testspiel.contactEmail}` : ''}
${testspiel.contactPhone ? `Telefon: ${testspiel.contactPhone}` : ''}
    `.trim()

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    window.open(mailtoLink)
    */

    // New Netlify email function
    try {
      const { sendEmail, createTestspielEmail } = await import('../utils/emailService')
      const emailData = createTestspielEmail(testspiel, user?.email || '')
      const result = await sendEmail(emailData)
      
      if (!result.success) {
        console.error('Failed to send email:', result.error)
        alert(`Fehler beim Senden der E-Mail: ${result.error}`)
      } else {
        console.log('Email sent successfully via Netlify function')
        alert('E-Mail erfolgreich versendet!')
      }
    } catch (error) {
      console.error('Error sending email:', error)
      alert('Fehler beim Senden der E-Mail')
    }
  }

  const getStatusBadge = (testspiel: any) => {
    if (testspiel.isSaved) {
      return (
        <span style={{
          background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
          color: 'white',
          fontSize: '12px',
          fontWeight: '600',
          padding: '4px 12px',
          borderRadius: '8px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
        }}>
          Gespeichert
        </span>
      )
    }
    
    switch (testspiel.status) {
      case 'confirmed':
        return (
          <span style={{
            background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            padding: '4px 12px',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
          }}>
            Bestätigt
          </span>
        )
      case 'pending':
        return (
          <span style={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            padding: '4px 12px',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
          }}>
            Ausstehend
          </span>
        )
      case 'cancelled':
        return (
          <span style={{
            background: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            padding: '4px 12px',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
          }}>
            Abgesagt
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '800',
          background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px',
          letterSpacing: '-0.025em'
        }}>
          Testspiele
        </h1>
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <Link 
            to="/testspiele/add" 
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
          >
            <Plus size={16} />
            Neues Testspiel
          </Link>
        </div>
      </div>

      {/* Termin-Verwaltung: nur Admins. Hierher verschoben (statt Admin-Seite),
          weil Termine dort gepflegt werden, wo Testspiele geplant werden. */}
      {profile?.role === 'admin' && (
        <div style={{ marginBottom: '24px' }}>
          <TestspielSlotsManager />
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '64px 20px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #1B7E3A',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
          <p style={{
            color: '#6B7280',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Testspiele werden geladen...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '1px solid #fca5a5',
          color: '#dc2626',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '24px',
          fontWeight: '500'
        }}>
          Fehler beim Laden der Testspiele: {error}
        </div>
      )}

      {!loading && (
        <>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            marginBottom: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setFilter('upcoming')}
                style={{
                  background: filter === 'upcoming' 
                    ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                    : 'transparent',
                  color: filter === 'upcoming' ? 'white' : '#374151',
                  border: filter === 'upcoming' ? 'none' : '1px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: filter === 'upcoming' 
                    ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (filter !== 'upcoming') {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.backgroundColor = '#f0fdf4'
                  }
                }}
                onMouseLeave={(e) => {
                  if (filter !== 'upcoming') {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                Bevorstehend ({testspiele.filter(t => new Date(t.date) >= now).length})
              </button>
              <button
                onClick={() => setFilter('past')}
                style={{
                  background: filter === 'past' 
                    ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                    : 'transparent',
                  color: filter === 'past' ? 'white' : '#374151',
                  border: filter === 'past' ? 'none' : '1px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: filter === 'past' 
                    ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (filter !== 'past') {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.backgroundColor = '#f0fdf4'
                  }
                }}
                onMouseLeave={(e) => {
                  if (filter !== 'past') {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                Vergangen ({testspiele.filter(t => new Date(t.date) < now).length})
              </button>
              <button
                onClick={() => setFilter('all')}
                style={{
                  background: filter === 'all' 
                    ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                    : 'transparent',
                  color: filter === 'all' ? 'white' : '#374151',
                  border: filter === 'all' ? 'none' : '1px solid #d1d5db',
                  borderRadius: '12px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: filter === 'all' 
                    ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (filter !== 'all') {
                    e.currentTarget.style.borderColor = '#1B7E3A'
                    e.currentTarget.style.backgroundColor = '#f0fdf4'
                  }
                }}
                onMouseLeave={(e) => {
                  if (filter !== 'all') {
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                Alle ({testspiele.length})
              </button>
            </div>
          </div>      {filteredTestspiele.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px 20px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          border: '1px solid #e5e7eb'
        }}>
          <Calendar size={64} style={{ 
            margin: '0 auto 16px', 
            color: '#d1d5db' 
          }} />
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '8px'
          }}>
            Keine Testspiele gefunden
          </h3>
          <p style={{
            color: '#6B7280',
            fontSize: '16px',
            fontWeight: '500',
            marginBottom: '24px'
          }}>
            {filter === 'upcoming' 
              ? 'Es sind keine bevorstehenden Testspiele geplant.'
              : filter === 'past'
              ? 'Es wurden noch keine Testspiele durchgeführt.'
              : 'Es sind noch keine Testspiele angelegt.'
            }
          </p>
          <Link 
            to="/testspiele/add" 
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)',
              textDecoration: 'none',
              marginTop: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
          >
            <Plus size={16} />
            Erstes Testspiel anlegen
          </Link>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '16px'
        }}>
          {filteredTestspiele.map((testspiel) => (
            <div key={testspiel.id} style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #e5e7eb',
              padding: '24px',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 12px 20px -5px rgba(0, 0, 0, 0.1), 0 8px 8px -5px rgba(0, 0, 0, 0.04)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            >
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ flex: '1' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#111827',
                      letterSpacing: '-0.025em',
                      margin: '0'
                    }}>
                      {testspiel.team} vs {testspiel.opponent}
                    </h3>
                    {getStatusBadge(testspiel)}
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#6B7280',
                    fontWeight: '500'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Calendar size={16} />
                      <span>
                        {format(new Date(testspiel.date), 'EEEE, d. MMMM yyyy', { locale: de })}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Clock size={16} />
                      <span>{testspiel.time} Uhr</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <MapPin size={16} />
                      <span>{testspiel.location || 'Ort nicht angegeben'}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <User size={16} />
                      <span>{testspiel.trainerName}</span>
                    </div>
                  </div>
                  
                  {testspiel.notes && (
                    <p style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      marginTop: '12px',
                      padding: '12px',
                      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                      borderRadius: '8px',
                      fontWeight: '500',
                      lineHeight: '1.5'
                    }}>
                      {testspiel.notes}
                    </p>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginLeft: '16px',
                  flexWrap: 'wrap'
                }}>
                  {/* VIEW BUTTON - FIRST */}
                  <button 
                    onClick={(e) => {
                      console.log('🔴 VIEW BUTTON CLICKED!')
                      e.preventDefault()
                      e.stopPropagation()
                      handleView(testspiel)
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      color: '#374151'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.backgroundColor = '#f0fdf4'
                      e.currentTarget.style.color = '#1B7E3A'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = '#374151'
                    }}
                    title="Details anzeigen"
                  >
                    <Eye size={16} />
                  </button>
                  
                  {/* EMAIL BUTTON - SECOND */}
                  <button 
                    onClick={(e) => {
                      console.log('📧 EMAIL BUTTON CLICKED!')
                      e.preventDefault()
                      e.stopPropagation()
                      handleSendEmail(testspiel)
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      color: '#374151'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.backgroundColor = '#f0fdf4'
                      e.currentTarget.style.color = '#1B7E3A'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = '#374151'
                    }}
                    title="E-Mail senden"
                  >
                    <Mail size={16} />
                  </button>
                  
                  {/* DELETE BUTTON - THIRD */}
                  <button 
                    onClick={(e) => {
                      console.log('🗑️ DELETE BUTTON CLICKED!')
                      e.preventDefault()
                      e.stopPropagation()
                      handleDelete(testspiel.id)
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 38, 38, 0.4)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.3)'
                    }}
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {/* Detail Modal */}
      {showModal && selectedTestspiel && (
        <div 
          style={{
            position: 'fixed',
            inset: '0',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '50',
            backdropFilter: 'blur(4px)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              console.log('❌ Closing modal via backdrop click')
              setShowModal(false)
              setSelectedTestspiel(null)
            }
          }}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              margin: '0 16px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e5e7eb',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.025em'
              }}>
                Testspiel Details
              </h2>
              <button 
                onClick={() => {
                  console.log('❌ Closing modal')
                  setShowModal(false)
                  setSelectedTestspiel(null)
                }}
                style={{
                  color: '#6b7280',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.backgroundColor = '#f3f4f6'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Team
                  </label>
                  <p style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {selectedTestspiel.team}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Gegner
                  </label>
                  <p style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    {selectedTestspiel.opponent}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Datum
                  </label>
                  <p style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#111827'
                  }}>
                    <Calendar size={16} style={{ marginRight: '8px', color: '#1B7E3A' }} />
                    {format(new Date(selectedTestspiel.date), 'PPP', { locale: de })}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Uhrzeit
                  </label>
                  <p style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#111827'
                  }}>
                    <Clock size={16} style={{ marginRight: '8px', color: '#1B7E3A' }} />
                    {selectedTestspiel.time}
                  </p>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  Spielort
                </label>
                <p style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#111827'
                }}>
                  <MapPin size={16} style={{ marginRight: '8px', color: '#1B7E3A' }} />
                  {selectedTestspiel.location || 'Vereinsplatz FC Frick'}
                </p>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '4px'
                }}>
                  Trainer/Verantwortlicher
                </label>
                <p style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#111827'
                }}>
                  <User size={16} style={{ marginRight: '8px', color: '#1B7E3A' }} />
                  {selectedTestspiel.trainerName}
                </p>
              </div>

              {selectedTestspiel.notes && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Notizen
                  </label>
                  <p style={{
                    background: '#F9FAFB',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#6B7280',
                    lineHeight: '1.5'
                  }}>
                    {selectedTestspiel.notes}
                  </p>
                </div>
              )}

              <div style={{
                borderTop: '1px solid #E5E7EB',
                paddingTop: '16px'
              }}>
                <h3 style={{
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '8px',
                  fontSize: '16px'
                }}>
                  Kontaktinformationen
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {selectedTestspiel.contactPerson && (
                    <p style={{ fontSize: '14px', color: '#6B7280' }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>Person:</span> {selectedTestspiel.contactPerson}
                    </p>
                  )}
                  {selectedTestspiel.contactEmail && (
                    <p style={{ fontSize: '14px', color: '#6B7280' }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>E-Mail:</span> {selectedTestspiel.contactEmail}
                    </p>
                  )}
                  {selectedTestspiel.contactPhone && (
                    <p style={{ fontSize: '14px', color: '#6B7280' }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>Telefon:</span> {selectedTestspiel.contactPhone}
                    </p>
                  )}
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '8px',
                paddingTop: '16px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <button 
                  onClick={() => handleSendEmail(selectedTestspiel)}
                  style={{
                    background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(27, 126, 58, 0.3)',
                    transform: 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(27, 126, 58, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(27, 126, 58, 0.3)'
                  }}
                >
                  <Mail style={{ width: '16px', height: '16px' }} />
                  E-Mail senden
                </button>
                <button 
                  onClick={() => {
                    console.log('❌ Closing modal via button')
                    setShowModal(false)
                    setSelectedTestspiel(null)
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)',
                    transform: 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(107, 114, 128, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(107, 114, 128, 0.3)'
                  }}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}